"use client";

import { useState, type FormEvent } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/Button";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

type Props = {
  /** Footer-style compact variant — smaller, tighter layout. */
  compact?: boolean;
  /** Optional inline label rendered above the input (compact only). */
  label?: string;
};

// Sprint 7 §2.8 microcopy (agent-default — flagged in handoff_2 for operator review):
//   Privacy line below submit + marketing-opt-in checkbox in the default variant.
//   The Fix Pack PDF §6 has the draft text; without an operator return, these
//   strings are the obvious neutral defaults that the operator can edit later.
const PRIVACY_NOTICE_PREFIX = "By submitting, you agree to our ";
const MARKETING_OPT_IN_LABEL = "Send me tournament announcements and league updates.";

export function WaitlistForm({ compact = false, label }: Props = {}) {
  const [email, setEmail] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  // Honeypot — bots fill any named input; humans don't see this field.
  const [hp, setHp] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus({ kind: "submitting" });
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          marketingOptIn,
          _hp: hp,
          consentAt: new Date().toISOString(),
        }),
      });
      const data = (await res.json()) as { ok: boolean; error?: string };
      if (data.ok) {
        setStatus({ kind: "success" });
        setEmail("");
      } else {
        setStatus({ kind: "error", message: data.error ?? "Something went wrong." });
      }
    } catch {
      setStatus({ kind: "error", message: "Network error. Try again." });
    }
  }

  // Hidden honeypot input — shared across both variants.
  const honeypot = (
    <div
      aria-hidden
      style={{
        position: "absolute",
        left: "-9999px",
        width: 1,
        height: 1,
        overflow: "hidden",
      }}
    >
      <label htmlFor="waitlist-company">Company (do not fill)</label>
      <input
        id="waitlist-company"
        type="text"
        name="_hp_company"
        tabIndex={-1}
        autoComplete="off"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
      />
    </div>
  );

  if (status.kind === "success") {
    return (
      <p
        role="status"
        className={
          compact
            ? "font-body text-sm text-gold"
            : "font-body text-base text-gold"
        }
      >
        You&apos;re on the list. Check your inbox.
      </p>
    );
  }

  if (compact) {
    const inputId = "waitlist-email-compact";
    return (
      <form onSubmit={onSubmit} className="relative space-y-2">
        {honeypot}
        {label && (
          <label
            htmlFor={inputId}
            className="block font-body text-xs text-fg-muted"
          >
            {label}
          </label>
        )}
        <div className="flex gap-2">
          <input
            id={inputId}
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="min-w-0 flex-1 rounded-sm border border-line bg-base px-3 py-2 font-body text-sm text-fg placeholder:text-fg-faint focus:border-gold focus:outline-none"
          />
          <Button
            variant="gold-filled"
            size="sm"
            type="submit"
            disabled={status.kind === "submitting"}
          >
            {status.kind === "submitting" ? "…" : "Join"}
          </Button>
        </div>
        <p className="font-body text-[11px] text-fg-faint">
          {PRIVACY_NOTICE_PREFIX}
          <Link
            href="/legal/privacy"
            className="underline-offset-2 hover:text-gold hover:underline"
          >
            Privacy Policy
          </Link>
          .
        </p>
        {status.kind === "error" && (
          <p role="alert" className="font-body text-xs text-crimson">
            {status.message}
          </p>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit} className="relative flex flex-col gap-4">
      {honeypot}
      <div className="flex flex-col gap-3 sm:flex-row">
        <label htmlFor="waitlist-email" className="sr-only">
          Email address
        </label>
        <input
          id="waitlist-email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="flex-1 rounded-sm border border-line bg-base px-4 py-3 font-body text-base text-fg placeholder:text-fg-faint focus:border-gold focus:outline-none"
        />
        <Button
          variant="gold-filled"
          type="submit"
          disabled={status.kind === "submitting"}
        >
          {status.kind === "submitting" ? "Submitting…" : "Get Announcements"}
        </Button>
      </div>
      <label className="flex items-start gap-3 font-body text-sm text-fg-muted">
        <input
          type="checkbox"
          name="marketingOptIn"
          checked={marketingOptIn}
          onChange={(e) => setMarketingOptIn(e.target.checked)}
          className="mt-1 h-4 w-4 cursor-pointer accent-gold"
        />
        <span>{MARKETING_OPT_IN_LABEL}</span>
      </label>
      <p className="font-body text-xs text-fg-faint">
        {PRIVACY_NOTICE_PREFIX}
        <Link
          href="/legal/privacy"
          className="underline-offset-2 hover:text-gold hover:underline"
        >
          Privacy Policy
        </Link>
        .
      </p>
      {status.kind === "error" && (
        <p role="alert" className="font-body text-sm text-crimson">
          {status.message}
        </p>
      )}
    </form>
  );
}
